/**
 * The platform's own confirmation, replacing `window.confirm`.
 *
 * ⛔ AMIR'S FINDING 7, 2026-09-12: *"the unsaved-changes prompt is a browser
 * alert. It must be a platform modal, consistent with everything else."*
 *
 * ┌─ ⚠️ AND THE HALF THAT CANNOT BE DELIVERED, SAID RATHER THAN PROMISED ───────┐
 * │ `beforeunload` CANNOT BE STYLED AND CANNOT BE REPLACED. Closing a tab,      │
 * │ reloading, or following a link off the site fires it, and the browser draws │
 * │ its own dialog with its own words - deliberately, because a page that could │
 * │ style that prompt could also imitate the browser.                          │
 * │                                                                            │
 * │ ⛔ SO THE SPLIT IS: this modal covers IN-APP navigation, which is every     │
 * │ route change and the Back button, and **the browser's dialog remains for    │
 * │ leaving the site entirely.** A person will therefore see two different      │
 * │ prompts for two different actions, and that is a limitation of the platform │
 * │ rather than a decision anybody took.                                       │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ THE SCRIM AND `role="dialog"` FOLLOW `CommandPalette`, which is the pattern
 * already in the tree - one modal idiom rather than a second one invented here.
 */
import { useEffect, useRef } from 'react'
import './confirm.css'

export type ConfirmDialogProps = {
  title: string
  body: string
  /** The destructive or forward action. Rendered as the accented button. */
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null)

  /**
   * ⛔ FOCUS LANDS ON CANCEL, NOT ON CONFIRM. This dialog appears because
   * somebody is about to lose work, so the key they press by reflex - Enter,
   * arriving on whatever has focus - must be the one that keeps it.
   *
   * ⚠️ AND ESCAPE IS CANCEL, which is the one keyboard convention a dialog does
   * not get to reinterpret.
   */
  useEffect(() => {
    cancelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div
      className="confirm__scrim"
      data-testid="confirm-scrim"
      /** ⚠️ Clicking away is Cancel, for the reason Escape is: the safe answer is
       *  the one an accident reaches. `role` is on the dialog, not here. */
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="confirm" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <h2 className="confirm__title" id="confirm-title">
          {title}
        </h2>
        <p className="confirm__body">{body}</p>
        <div className="confirm__actions">
          <button
            ref={cancelRef}
            type="button"
            className="confirm__cancel"
            data-testid="confirm-cancel"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="confirm__confirm"
            data-testid="confirm-confirm"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
